import { useTranslation } from "react-i18next";
import type {
  ObjectKind,
  SchemaElement,
} from "../../api/restaurantSchema";
import { BLOCKS, blockDepthPx, blockShadow } from "./blockDepth";
import { ELEMENT_STYLES } from "./elementCatalog";
import EntranceDoor from "./EntranceDoor";
import { DESIGN_UNIT, objectDimensions } from "./geometry";
import SelectionHandles from "./SelectionHandles";
import { SELECTION_RING } from "./tableTones";

/**
 * Обект отгоре по дизайна — блокче със страна и сянка върху пода, като
 * масите. Стената е плътна ивица; входът е врата (`EntranceDoor`);
 * останалите са тонални плочки с икона и надпис. Размерите са логически
 * единици × `scale` — inline style само за тях (изключение (1)).
 */
const ObjectView = ({
  element,
  scale,
  selected,
}: {
  element: SchemaElement;
  scale: number;
  selected: boolean;
}) => {
  const { t } = useTranslation();
  const kind = element.kind as ObjectKind;
  const designPixel = DESIGN_UNIT * scale;

  if (kind === "entrance") {
    return <EntranceDoor element={element} scale={scale} selected={selected} />;
  }

  const style = ELEMENT_STYLES[kind];
  const { width, height } = objectDimensions(kind, element.size);

  if (kind === "wall") {
    return (
      <div
        className={`relative bg-schema-wall ${
          selected ? "outline outline-[2.5px] outline-offset-2 outline-brand" : ""
        }`}
        style={{
          width: width * scale,
          height: Math.max(3, height * scale),
          borderRadius: designPixel,
          boxShadow: blockShadow({
            rotation: element.rotation,
            depth: blockDepthPx(BLOCKS.wall.depth, designPixel),
            color: BLOCKS.wall.side,
          }),
        }}
      >
        {selected && <SelectionHandles />}
      </div>
    );
  }

  const isPlant = kind === "plant";
  return (
    <div
      className={`relative flex items-center justify-center gap-2 border-[1.5px] font-bold transition-[border-color,box-shadow] duration-150 ${style.bodyClass} ${
        isPlant ? "rounded-full" : ""
      } ${selected ? "!border-brand" : ""}`}
      style={{
        width: width * scale,
        height: height * scale,
        borderRadius: isPlant ? undefined : 12 * designPixel,
        fontSize: Math.max(10, 15 * designPixel),
        boxShadow: blockShadow({
          rotation: element.rotation,
          depth: blockDepthPx(BLOCKS[kind].depth, designPixel),
          color: BLOCKS[kind].side,
          ring: selected ? SELECTION_RING : null,
        }),
      }}
    >
      <div
        className="flex items-center gap-[0.5em]"
        style={{ transform: `rotate(${-element.rotation}deg)` }}
      >
        <style.Icon
          className={isPlant ? "h-[1.35em] w-[1.35em]" : "h-[1.2em] w-[1.2em]"}
        />
        {!isPlant && width * scale > 60 && (
          <span className="whitespace-nowrap">{t(style.labelKey)}</span>
        )}
      </div>
      {selected && <SelectionHandles />}
    </div>
  );
};

export default ObjectView;
